import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CARGO_LABELS, CARGO_LIST, VEHICLE_TYPES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, Leaf } from "lucide-react";

const EF = { furgone: 0.30, motrice: 0.70, bilico: 0.90, autoarticolato: 0.95, centinato: 0.85, frigo: 1.05, isotermico: 1.00 };

const empty = {
  origin: "", destination: "", stopsText: "", cargo_types: [],
  departure_date: "", arrival_date: "", price_min: "", price_max: "",
  partial_available: false, vehicle_type: "", distance_km: "", capacity_note: "", notes: "",
};

export function RouteForm({ onCreated }) {
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const toggleCargo = (c) => setF((p) => ({
    ...p, cargo_types: p.cargo_types.includes(c) ? p.cargo_types.filter((x) => x !== c) : [...p.cargo_types, c],
  }));

  const submit = async () => {
    if (!f.origin || !f.destination || !f.departure_date || f.cargo_types.length === 0) {
      toast.error("Compila origine, destinazione, data e almeno un tipo di merce");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        origin: f.origin, destination: f.destination,
        stops: f.stopsText.split(",").map((s) => s.trim()).filter(Boolean),
        cargo_types: f.cargo_types,
        departure_date: f.departure_date, arrival_date: f.arrival_date || null,
        price_min: Number(f.price_min || 0), price_max: Number(f.price_max || 0),
        partial_available: f.partial_available, vehicle_type: f.vehicle_type || null,
        distance_km: Number(f.distance_km || 0),
        capacity_note: f.capacity_note || null, notes: f.notes || null,
      };
      await api.post("/routes", payload);
      toast.success("Tratta pubblicata!");
      setF(empty);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="route-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Origine *</Label>
          <Input value={f.origin} onChange={(e) => set("origin", e.target.value)} placeholder="es. Milano" className="rounded-sm mt-1" data-testid="route-origin-input" />
        </div>
        <div>
          <Label className="text-xs">Destinazione *</Label>
          <Input value={f.destination} onChange={(e) => set("destination", e.target.value)} placeholder="es. Napoli" className="rounded-sm mt-1" data-testid="route-destination-input" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Tappe intermedie (separate da virgola)</Label>
        <Input value={f.stopsText} onChange={(e) => set("stopsText", e.target.value)} placeholder="es. Bologna, Firenze, Roma" className="rounded-sm mt-1" data-testid="route-stops-input" />
      </div>

      <div>
        <Label className="text-xs mb-2 block">Tipologie di merce trasportabili *</Label>
        <div className="flex flex-wrap gap-2">
          {CARGO_LIST.map((c) => (
            <button key={c} type="button" onClick={() => toggleCargo(c)} data-testid={`route-cargo-${c}`}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${f.cargo_types.includes(c) ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}>
              {CARGO_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Data partenza *</Label>
          <Input type="date" value={f.departure_date} onChange={(e) => set("departure_date", e.target.value)} className="rounded-sm mt-1" data-testid="route-departure-input" />
        </div>
        <div>
          <Label className="text-xs">Data arrivo prevista</Label>
          <Input type="date" value={f.arrival_date} onChange={(e) => set("arrival_date", e.target.value)} className="rounded-sm mt-1" data-testid="route-arrival-input" />
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Prezzo min (€)</Label>
          <Input type="number" value={f.price_min} onChange={(e) => set("price_min", e.target.value)} className="rounded-sm mt-1" data-testid="route-pricemin-input" />
        </div>
        <div>
          <Label className="text-xs">Prezzo max (€)</Label>
          <Input type="number" value={f.price_max} onChange={(e) => set("price_max", e.target.value)} className="rounded-sm mt-1" data-testid="route-pricemax-input" />
        </div>
        <div>
          <Label className="text-xs">Mezzo</Label>
          <Select value={f.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="route-vehicle-select"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between border border-border rounded-sm p-3">
        <div>
          <span className="font-display font-semibold text-sm">Disponibile a trasporti parziali</span>
          <p className="text-xs text-muted-foreground">Accetto carichi su una porzione della tratta</p>
        </div>
        <Switch checked={f.partial_available} onCheckedChange={(v) => set("partial_available", v)} data-testid="route-partial-switch" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Distanza tratta (km)</Label>
          <Input type="number" min="0" value={f.distance_km} onChange={(e) => set("distance_km", e.target.value)} placeholder="es. 770" className="rounded-sm mt-1" data-testid="route-distance-input" />
          {f.distance_km > 0 && (
            <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
              <Leaf size={12} /> ~{Math.round(Number(f.distance_km) * (EF[f.vehicle_type] || 0.8))} kg di CO₂ risparmiata
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs">Capacità disponibile</Label>
          <Input value={f.capacity_note} onChange={(e) => set("capacity_note", e.target.value)} placeholder="es. 3 pallet, 500 kg, 4 m³" className="rounded-sm mt-1" data-testid="route-capacity-input" />
        </div>
      </div>

      <div>
        <Label className="text-xs">Note aggiuntive</Label>
        <Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="rounded-sm mt-1" data-testid="route-notes-input" />
      </div>

      <Button onClick={submit} disabled={loading} className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold w-full gap-2" data-testid="route-submit-btn">
        <Plus size={16} /> {loading ? "Pubblicazione…" : "Pubblica tratta"}
      </Button>
    </div>
  );
}
